/**
 * ExerciseCreator — Bottom-sheet form to create a custom exercise
 *
 * Fields: name, category, movement pattern, primary muscle,
 *         up to 2 secondary muscles with fraction selectors,
 *         equipment (multi-select), rest time.
 *
 * Saves to Dexie exercises table with isBuiltIn: false.
 */

import { useState } from 'react';
import { v4 as uuid } from 'uuid';
import { db } from '@/lib/db';
import type {
  Exercise, MuscleGroup, Equipment,
  MovementPattern, ExerciseCategory,
} from '@/types';

// ── Option lists ──────────────────────────────────────────────

const MUSCLE_OPTIONS: { value: MuscleGroup; label: string }[] = [
  { value: 'chest',        label: 'Chest' },
  { value: 'front_delt',   label: 'Front Delt' },
  { value: 'lateral_delt', label: 'Lateral Delt' },
  { value: 'rear_delt',    label: 'Rear Delt' },
  { value: 'triceps',      label: 'Triceps' },
  { value: 'biceps',       label: 'Biceps' },
  { value: 'forearms',     label: 'Forearms' },
  { value: 'lats',         label: 'Lats' },
  { value: 'traps',        label: 'Traps' },
  { value: 'rhomboids',    label: 'Rhomboids' },
  { value: 'lower_back',   label: 'Lower Back' },
  { value: 'quads',        label: 'Quads' },
  { value: 'hamstrings',   label: 'Hamstrings' },
  { value: 'glutes',       label: 'Glutes' },
  { value: 'calves',       label: 'Calves' },
  { value: 'core',         label: 'Core' },
  { value: 'hip_flexors',  label: 'Hip Flexors' },
];

const EQUIPMENT_OPTIONS: { value: Equipment; label: string; icon: string }[] = [
  { value: 'barbell',    label: 'Barbell',    icon: '🏋️' },
  { value: 'dumbbell',  label: 'Dumbbell',   icon: '💪' },
  { value: 'cable',     label: 'Cable',      icon: '🔗' },
  { value: 'machine',   label: 'Machine',    icon: '⚙️' },
  { value: 'kettlebell',label: 'Kettlebell', icon: '🔔' },
  { value: 'ez_bar',    label: 'EZ Bar',     icon: '〰️' },
  { value: 'bodyweight',label: 'Bodyweight', icon: '🤸' },
  { value: 'bands',     label: 'Bands',      icon: '🎗️' },
];

const PATTERN_OPTIONS: { value: MovementPattern; label: string }[] = [
  { value: 'horizontal_push',    label: 'Horizontal Push' },
  { value: 'horizontal_pull',    label: 'Horizontal Pull' },
  { value: 'vertical_push',      label: 'Vertical Push' },
  { value: 'vertical_pull',      label: 'Vertical Pull' },
  { value: 'hip_hinge',          label: 'Hip Hinge' },
  { value: 'squat',              label: 'Squat' },
  { value: 'lunge',              label: 'Lunge' },
  { value: 'isolation_push',     label: 'Isolation Push' },
  { value: 'isolation_pull',     label: 'Isolation Pull' },
  { value: 'core_anti_extension',label: 'Core (Anti-Extension)' },
  { value: 'core_rotation',      label: 'Core (Rotation)' },
  { value: 'carry',              label: 'Carry' },
];

// ── Component ─────────────────────────────────────────────────

interface ExerciseCreatorProps {
  onClose: () => void;
  onSaved?: (exercise: Exercise) => void;
}

export default function ExerciseCreator({ onClose, onSaved }: ExerciseCreatorProps) {
  const [name, setName]           = useState('');
  const [category, setCategory]   = useState<ExerciseCategory>('compound');
  const [pattern, setPattern]     = useState<MovementPattern>('horizontal_push');
  const [primary, setPrimary]     = useState<MuscleGroup>('chest');
  const [sec1, setSec1]           = useState<MuscleGroup | ''>('');
  const [sec1Frac, setSec1Frac]   = useState(0.5);
  const [sec2, setSec2]           = useState<MuscleGroup | ''>('');
  const [sec2Frac, setSec2Frac]   = useState(0.5);
  const [equipment, setEquipment] = useState<Equipment[]>(['barbell']);
  const [restSeconds, setRestSeconds] = useState(90);
  const [saving, setSaving]       = useState(false);
  const [error, setError]         = useState('');

  const toggleEquipment = (eq: Equipment) => {
    setEquipment((prev) =>
      prev.includes(eq) ? prev.filter((e) => e !== eq) : [...prev, eq],
    );
  };

  const handleSave = async () => {
    if (!name.trim()) { setError('Name is required'); return; }
    if (equipment.length === 0) { setError('Select at least one equipment type'); return; }

    setSaving(true);
    const muscles = [{ muscle: primary, fraction: 1.0 }];
    if (sec1) muscles.push({ muscle: sec1, fraction: sec1Frac });
    if (sec2) muscles.push({ muscle: sec2, fraction: sec2Frac });

    // Determine split day from pattern
    const legsPatterns: MovementPattern[] = ['squat', 'lunge'];
    const anteriorPatterns: MovementPattern[] = [
      'horizontal_push', 'vertical_push', 'isolation_push',
      'core_anti_extension', 'core_rotation',
    ];
    const splitDay = legsPatterns.includes(pattern)
      ? 'legs'
      : anteriorPatterns.includes(pattern) ? 'anterior' : 'posterior';

    const exercise: Exercise = {
      id: `custom_${uuid()}`,
      name: name.trim(),
      category,
      pattern,
      splitDay,
      equipment,
      muscles,
      restSeconds,
      warmupEligible: category === 'compound',
      isBuiltIn: false,
    };

    await db.exercises.put(exercise);
    setSaving(false);
    onSaved?.(exercise);
    onClose();
  };

  const selectClass = `w-full bg-surface-300 rounded-xl px-3 py-3 text-sm text-ink
    focus:outline-none focus:ring-2 focus:ring-accent min-h-tap`;

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center pb-safe">
      <button className="absolute inset-0 bg-black/60" onClick={onClose} />

      <div className="relative w-full max-w-lg bg-surface-100 rounded-t-3xl
                      pt-4 pb-6 shadow-card-lg animate-slide-up
                      max-h-[90vh] flex flex-col">
        {/* Handle */}
        <div className="w-10 h-1 bg-surface-400 rounded-full mx-auto mb-4 flex-shrink-0" />

        {/* Header */}
        <div className="flex items-center justify-between px-6 mb-4 flex-shrink-0">
          <h3 className="text-lg font-bold text-ink">New Exercise</h3>
          <button className="btn-ghost p-1 text-lg" onClick={onClose}>✕</button>
        </div>

        {/* Scrollable form */}
        <div className="overflow-y-auto flex-1 px-6 space-y-4 no-scrollbar">

          {/* Name */}
          <div>
            <p className="text-xs text-ink-muted mb-1.5 font-medium">Exercise Name</p>
            <input
              type="text"
              value={name}
              onChange={(e) => { setName(e.target.value); setError(''); }}
              placeholder="e.g. Incline Cable Fly"
              className={`${selectClass} placeholder:text-ink-faint`}
            />
          </div>

          {/* Category */}
          <div>
            <p className="text-xs text-ink-muted mb-1.5 font-medium">Category</p>
            <div className="flex gap-2">
              {(['compound', 'isolation'] as const).map((c) => (
                <button key={c}
                  onClick={() => setCategory(c)}
                  className={`flex-1 py-3 rounded-xl text-sm font-semibold transition-colors
                    ${category === c ? 'bg-accent text-white' : 'bg-surface-300 text-ink-muted'}`}>
                  {c.charAt(0).toUpperCase() + c.slice(1)}
                </button>
              ))}
            </div>
          </div>

          {/* Movement pattern */}
          <div>
            <p className="text-xs text-ink-muted mb-1.5 font-medium">Movement Pattern</p>
            <select value={pattern} onChange={(e) => setPattern(e.target.value as MovementPattern)}
              className={selectClass}>
              {PATTERN_OPTIONS.map((p) => (
                <option key={p.value} value={p.value}>{p.label}</option>
              ))}
            </select>
          </div>

          {/* Primary muscle */}
          <div>
            <p className="text-xs text-ink-muted mb-1.5 font-medium">Primary Muscle</p>
            <select value={primary} onChange={(e) => setPrimary(e.target.value as MuscleGroup)}
              className={selectClass}>
              {MUSCLE_OPTIONS.map((m) => (
                <option key={m.value} value={m.value}>{m.label}</option>
              ))}
            </select>
          </div>

          {/* Secondary muscles */}
          <div>
            <p className="text-xs text-ink-muted mb-1.5 font-medium">
              Secondary Muscles <span className="text-ink-faint">(optional)</span>
            </p>
            <div className="space-y-2">
              {[
                { val: sec1, setVal: setSec1, frac: sec1Frac, setFrac: setSec1Frac },
                { val: sec2, setVal: setSec2, frac: sec2Frac, setFrac: setSec2Frac },
              ].map(({ val, setVal, frac, setFrac }, idx) => (
                <div key={idx} className="flex gap-2">
                  <select
                    value={val}
                    onChange={(e) => setVal(e.target.value as MuscleGroup | '')}
                    className={`flex-1 ${selectClass}`}
                  >
                    <option value="">None</option>
                    {MUSCLE_OPTIONS.filter((m) => m.value !== primary).map((m) => (
                      <option key={m.value} value={m.value}>{m.label}</option>
                    ))}
                  </select>
                  {val && (
                    <div className="flex bg-surface-300 rounded-xl p-0.5 gap-0.5">
                      {([0.25, 0.5, 0.75] as const).map((f) => (
                        <button key={f}
                          onClick={() => setFrac(f)}
                          className={`px-2.5 py-1.5 rounded-lg text-xs font-bold transition-colors
                            ${frac === f ? 'bg-accent text-white' : 'text-ink-muted'}`}>
                          {f}×
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Equipment */}
          <div>
            <p className="text-xs text-ink-muted mb-1.5 font-medium">Equipment</p>
            <div className="flex flex-wrap gap-2">
              {EQUIPMENT_OPTIONS.map((eq) => (
                <button
                  key={eq.value}
                  onClick={() => toggleEquipment(eq.value)}
                  className={`flex items-center gap-1.5 px-3 py-2 rounded-xl text-sm
                               font-medium transition-colors min-h-tap
                    ${equipment.includes(eq.value)
                      ? 'bg-accent text-white'
                      : 'bg-surface-300 text-ink-muted'}`}
                >
                  <span>{eq.icon}</span> {eq.label}
                </button>
              ))}
            </div>
          </div>

          {/* Rest time */}
          <div>
            <p className="text-xs text-ink-muted mb-1.5 font-medium">Rest Time</p>
            <div className="flex gap-2">
              {[60, 90, 120, 180].map((s) => (
                <button key={s}
                  onClick={() => setRestSeconds(s)}
                  className={`flex-1 py-3 rounded-xl text-sm font-semibold transition-colors
                    ${restSeconds === s ? 'bg-accent text-white' : 'bg-surface-300 text-ink-muted'}`}>
                  {s}s
                </button>
              ))}
            </div>
          </div>

          {error && (
            <p className="text-danger text-sm">{error}</p>
          )}
        </div>

        {/* Save button */}
        <div className="px-6 pt-4 flex-shrink-0">
          <button
            className="btn-primary w-full py-4"
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? 'Saving…' : 'Save Exercise'}
          </button>
        </div>
      </div>
    </div>
  );
}
